import {
    EmploymentType,
    EpisodeStatus,
    OrderType,
    PatientFilter,
    SyncState,
    VisitDatetimeField,
    VisitFilter,
    VisitOutcome,
    VisitState,
} from '@axle-health/graphql-schema';

import { PINNACLE_CONTAINER_KEYS } from '../containerKeys';
import {
    addCalendarDays,
    addDays,
    date,
    dateInDays,
    datetime,
    startOfDayInZone,
    startOfMedicareWeek,
} from '../graphInputHelpers';
import { BuilderContext, QueueDefinition, RootEntity } from '../types';
import {
    ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    AUTH_RELATED_SYNC_ERROR_TYPES,
    CMS_485_SERVICE_ID,
    DEFAULT_CONTAINER_FILTER_WINDOW_DAYS,
    DISCHARGE_SUMMARY_SERVICE_IDS,
    DISCIPLINE_READMISSION_SERVICE_IDS,
    EVAL_SERVICE_IDS,
    HHA_SUPERVISORY_SERVICE_IDS,
    HHA_VISIT_SERVICE_IDS,
    LPN_SUPERVISORY_SERVICE_IDS,
    LPN_VISIT_SERVICE_IDS,
    NOMNC_SERVICE_IDS,
    NON_VISIT_TASK_ONLY_SERVICE_IDS,
    OASIS_DISCHARGE_ONLY_SERVICE_IDS,
    OASIS_DISCHARGE_SERVICE_IDS,
    OASIS_E2_FOLLOWUP_SERVICE_IDS,
    OASIS_RECERT_SERVICE_IDS,
    OTA_VISIT_SERVICE_IDS,
    OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    OT_THERAPY_SUPERVISORY_SERVICE_IDS,
    PINNACLE_FULFILLMENT_WINDOW_END_CUTOFF,
    POST_DISCHARGE_EXCLUDED_SERVICE_IDS,
    POST_HOSPITAL_ORDER_SERVICE_ID,
    PTA_VISIT_SERVICE_IDS,
    PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    PT_THERAPY_SUPERVISORY_SERVICE_IDS,
    REASSESSMENT_SERVICE_IDS,
    ROC_SERVICE_IDS,
    SCIC_ORDER_SERVICE_ID,
    SOC_SERVICE_IDS,
    ST_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    TRANSFER_RESOLUTION_SERVICE_IDS,
    TRANSFER_SERVICE_IDS,
} from './pinnacleServiceIds';

// The episode gate several queues share: Active, ending today or later.
const episodeActivePresentOrFuture = (now: Date, timezone: string) => ({
    status: { exact: EpisodeStatus.ACTIVE },
    endDate: { gte: date(now, timezone) },
});

// The wider gate: Active or Pending, ending today or later.
const episodeActivePendingPresentOrFuture = (now: Date, timezone: string) => ({
    status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
    endDate: { gte: date(now, timezone) },
});

// Patients that need care teams assigned.
const INCOMPLETE_CARE_TEAM: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Team',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: {
            ...episodeActivePendingPresentOrFuture(now, timezone),
            hasVisits: {
                disciplineCoveredByCareTeam: { exact: false },
                outcome: { exact: VisitOutcome.NONE },
                NOT: {
                    disciplines: {
                        externalIds: { value: { inList: ['MISC', 'ORDERS', 'COMM'] } },
                    },
                },
            },
        },
    }),
};

// SOC visits that still need a clinician or a date, on an episode ending today or
// later.
const SOC: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.soc,
    label: 'SOC (Start of Care)',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
        needsAssignment: { exact: true },
        episode: episodeActivePendingPresentOrFuture(now, timezone),
    }),
};

// ROC visits that still need a clinician or a date, on an Active or On Hold
// episode ending today or later.
const ROC: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.roc,
    label: 'ROC (Resumption of Care)',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
        needsAssignment: { exact: true },
        episode: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.ON_HOLD] },
            endDate: { gte: date(now, timezone) },
        },
    }),
};

// Eval visits that still need a clinician or a date.
const EVAL: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.eval,
    label: 'Eval',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
        needsAssignment: { exact: true },
        episode: {
            status: {
                inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING, EpisodeStatus.ON_HOLD],
            },
            endDate: { gte: date(now, timezone) },
        },
    }),
};

// Visits in review that the patient did not show for, within the look-back window.
const POTENTIAL_RESCHEDULES: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.potentialReschedules,
    label: 'Potential Reschedules',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now }) => ({
        patient: { isActive: { exact: true } },
        startDatetime: {
            gte: datetime(addDays(now, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS)),
        },
        state: { exact: VisitState.IN_REVIEW },
        outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
    }),
};

// Visits still needing assignment whose fulfillment window closes within a week.
const URGENT_VISITS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.urgentVisitsNeedingScheduling,
    label: 'Urgent Visits Needing Scheduling',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        episode: episodeActivePresentOrFuture(now, timezone),
        needsAssignment: { exact: true },
        latestEndDatetime: {
            gte: date(now, timezone),
            lte: dateInDays(now, timezone, 7),
        },
    }),
};

// Episodes carrying at least one visit that still needs a service, clinician or date.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PINNACLE_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        ...episodeActivePresentOrFuture(now, timezone),
        hasVisits: {
            needsAssignment: { exact: true },
        },
    }),
};

// Frequency orders with more active visits than the order allows.
const OVERSCHEDULED_PATIENTS: QueueDefinition<RootEntity.ORDER> = {
    key: PINNACLE_CONTAINER_KEYS.overscheduledPatients,
    label: 'Overscheduled Patients',
    rootEntity: RootEntity.ORDER,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        orderType: { exact: OrderType.FREQUENCY },
        exceedsMaxActiveOccurrences: { exact: true },
        episode: episodeActivePresentOrFuture(now, timezone),
        NOT: {
            activeVisits: {
                services: {
                    externalIds: {
                        value: {
                            inList: [...DISCHARGE_SUMMARY_SERVICE_IDS, ...TRANSFER_SERVICE_IDS],
                        },
                    },
                },
            },
        },
    }),
};

// Frequency orders with fewer active visits than the order requires.
const UNDERSCHEDULED_PATIENTS: QueueDefinition<RootEntity.ORDER> = {
    key: PINNACLE_CONTAINER_KEYS.underscheduledPatients,
    label: 'Frequency Review Needed',
    rootEntity: RootEntity.ORDER,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        orderType: { exact: OrderType.FREQUENCY },
        exceedsMinActiveOccurrences: { exact: true },
        episode: episodeActivePresentOrFuture(now, timezone),
        fulfillmentWindowEndDate: { gt: PINNACLE_FULFILLMENT_WINDOW_END_CUTOFF },
    }),
};

// Pending visits assigned to a clinician who is no longer active.
const INACTIVE_CLINICIAN_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: { isActive: { exact: false } },
        episode: episodeActivePendingPresentOrFuture(now, timezone),
        effectiveDate: {
            gte: dateInDays(now, timezone, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
        },
    }),
};

// Agency-discharged patients who still have pending future visits. Rooted on
// visits so the count is pending visits to cancel, not patients. Discharge
// paperwork and non-visit tasks are excluded, since those are expected to remain.
const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.agencyDischargedPatientsWithPendingVisits,
    label: 'Agency Discharged Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: false } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: date(now, timezone) },
        NOT: {
            services: {
                externalIds: {
                    value: {
                        inList: [
                            ...OASIS_DISCHARGE_SERVICE_IDS,
                            ...POST_DISCHARGE_EXCLUDED_SERVICE_IDS,
                        ],
                    },
                },
            },
        },
    }),
};

// Pending visits left behind after a discipline discharge that a later
// same-discipline Eval or ROC has not re-admitted.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        NOT: {
            services: {
                externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
            },
        },
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            sameDiscipline: true,
            filter: {
                services: {
                    externalIds: { value: { inList: ALL_DISCIPLINE_DISCHARGE_SERVICE_IDS } },
                },
                outcome: { exact: VisitOutcome.COMPLETED },
                NOT: {
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        sameDiscipline: true,
                        filter: {
                            services: {
                                externalIds: {
                                    value: { inList: DISCIPLINE_READMISSION_SERVICE_IDS },
                                },
                            },
                            outcome: { exact: VisitOutcome.COMPLETED },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gtOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    ltOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Pending visits tied to a frequency order that has been discontinued.
const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits,
    label: 'Discontinued Frequencies with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        outcome: { exact: VisitOutcome.NONE },
        relatedOrder: {
            discontinuedAt: { lt: date(now, timezone) },
        },
    }),
};

// Pending future visits on an on-hold (transferred) episode, excluding the visits
// that would themselves resolve the transfer.
const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.transferredPatientsWithPendingVisits,
    label: 'Transferred Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: date(now, timezone) },
        episode: { status: { exact: EpisodeStatus.ON_HOLD } },
        NOT: {
            services: {
                externalIds: {
                    value: {
                        inList: [
                            ...TRANSFER_RESOLUTION_SERVICE_IDS,
                            ...POST_DISCHARGE_EXCLUDED_SERVICE_IDS,
                        ],
                    },
                },
            },
        },
    }),
};

type UpcomingSupeWithin = { daysAfterPriorSupe: number } | { daysFromToday: number };

const THERAPY_DISCIPLINE_DISCHARGE_SERVICE_IDS_MAP: Record<'PT' | 'OT' | 'ST', string[]> = {
    PT: PT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    OT: OT_DISCIPLINE_DISCHARGE_SERVICE_IDS,
    ST: ST_DISCIPLINE_DISCHARGE_SERVICE_IDS,
};

// A future scheduled discharge in a therapy discipline. Patients already on their
// way out are excluded from the supervisory and re-eval queues.
const pendingDischargeVisitFilter = (
    disciplineCode: 'PT' | 'OT' | 'ST',
    { now, timezone }: BuilderContext,
): VisitFilter => ({
    services: {
        externalIds: {
            value: {
                inList: [
                    ...OASIS_DISCHARGE_SERVICE_IDS,
                    ...THERAPY_DISCIPLINE_DISCHARGE_SERVICE_IDS_MAP[disciplineCode],
                ],
            },
        },
    },
    disciplines: { code: { exact: disciplineCode } },
    outcome: { exact: VisitOutcome.NONE },
    effectiveDate: { gte: date(now, timezone) },
});

// A future scheduled, fully-assigned supervisory visit that already covers the
// patient. Coverage has two shapes: within N days after a prior supervisory visit
// (HHA and LPN cadence), or inside [today, today + N] (therapy cadence).
const buildUpcomingSupeCoverageFilter = (
    supervisoryServiceIds: string[],
    disciplineFilter: VisitFilter,
    upcomingSupeWithin: UpcomingSupeWithin,
    ctx: BuilderContext,
): VisitFilter => {
    const today = date(ctx.now, ctx.timezone);
    const supervisoryServicesFilter = {
        services: { externalIds: { value: { inList: supervisoryServiceIds } } },
    };
    if ('daysAfterPriorSupe' in upcomingSupeWithin) {
        return {
            ...supervisoryServicesFilter,
            ...disciplineFilter,
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: today },
            needsAssignment: { exact: false },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                sameEpisode: false,
                filter: { ...supervisoryServicesFilter, ...disciplineFilter },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gteOffsetMinutes: -upcomingSupeWithin.daysAfterPriorSupe * 24 * 60,
                        lteOffsetMinutes: 0,
                    },
                ],
            },
        };
    }
    return {
        ...supervisoryServicesFilter,
        ...disciplineFilter,
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: {
            gte: today,
            lte: dateInDays(ctx.now, ctx.timezone, upcomingSupeWithin.daysFromToday),
        },
        needsAssignment: { exact: false },
    };
};

// A patient is pending a supervisory visit when they have a future anchor visit,
// no completed supervisory visit inside the look-back, and no future supervisory
// visit already covering them.
const pendingSupervisoryBranch = (
    {
        anchorServiceIds,
        supervisoryServiceIds,
        lookbackDays,
        upcomingSupeWithin,
        disciplineCode,
    }: {
        anchorServiceIds: string[];
        supervisoryServiceIds: string[];
        lookbackDays: number;
        upcomingSupeWithin: UpcomingSupeWithin;
        disciplineCode?: 'PT' | 'OT';
    },
    ctx: BuilderContext,
): PatientFilter => {
    const disciplineFilter: VisitFilter = disciplineCode
        ? { disciplines: { code: { exact: disciplineCode } } }
        : {};
    const today = date(ctx.now, ctx.timezone);
    const upcomingSupeFilter = buildUpcomingSupeCoverageFilter(
        supervisoryServiceIds,
        disciplineFilter,
        upcomingSupeWithin,
        ctx,
    );
    return {
        isActive: { exact: true },
        hasVisits: {
            services: { externalIds: { value: { inList: anchorServiceIds } } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: today },
        },
        NOT: {
            hasVisits: {
                services: { externalIds: { value: { inList: supervisoryServiceIds } } },
                ...disciplineFilter,
                outcome: { exact: VisitOutcome.COMPLETED },
                effectiveDate: {
                    gte: dateInDays(ctx.now, ctx.timezone, -lookbackDays),
                },
            },
        },
        AND: {
            NOT: { hasVisits: upcomingSupeFilter },
            ...(disciplineCode
                ? {
                      AND: {
                          NOT: { hasVisits: pendingDischargeVisitFilter(disciplineCode, ctx) },
                      },
                  }
                : {}),
        },
    };
};

// Patients with a future HHA visit and no supervisory visit covering it.
const PENDING_HHA_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.pendingHhaSupervisory,
    label: 'Pending HHA Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        hasEpisodes: episodeActivePresentOrFuture(ctx.now, ctx.timezone),
        ...pendingSupervisoryBranch(
            {
                anchorServiceIds: HHA_VISIT_SERVICE_IDS,
                supervisoryServiceIds: HHA_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 7,
                upcomingSupeWithin: { daysAfterPriorSupe: 14 },
            },
            ctx,
        ),
    }),
};

// Patients with a future LPN visit and no supervisory visit covering it.
const PENDING_LPN_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.pendingLpnSupervisory,
    label: 'Pending LPN Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        hasEpisodes: episodeActivePresentOrFuture(ctx.now, ctx.timezone),
        ...pendingSupervisoryBranch(
            {
                anchorServiceIds: LPN_VISIT_SERVICE_IDS,
                supervisoryServiceIds: LPN_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 23,
                upcomingSupeWithin: { daysAfterPriorSupe: 30 },
            },
            ctx,
        ),
    }),
};

// Patients with a future PTA or COTA visit and no therapy supervisory visit
// covering it, in either discipline.
const PENDING_THERAPY_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.pendingTherapySupervisory,
    label: 'Pending Therapy Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        ...pendingSupervisoryBranch(
            {
                anchorServiceIds: PTA_VISIT_SERVICE_IDS,
                supervisoryServiceIds: PT_THERAPY_SUPERVISORY_SERVICE_IDS,
                lookbackDays: 23,
                upcomingSupeWithin: { daysFromToday: 7 },
                disciplineCode: 'PT',
            },
            ctx,
        ),
        OR: {
            ...pendingSupervisoryBranch(
                {
                    anchorServiceIds: OTA_VISIT_SERVICE_IDS,
                    supervisoryServiceIds: OT_THERAPY_SUPERVISORY_SERVICE_IDS,
                    lookbackDays: 23,
                    upcomingSupeWithin: { daysFromToday: 7 },
                    disciplineCode: 'OT',
                },
                ctx,
            ),
        },
    }),
};

// One discipline's branch of the re-eval queue. Each is a complete PatientFilter,
// and the queue ORs three together so any uncovered discipline lights the patient up.
const pendingReEvalBranchForDiscipline = (
    disciplineCode: 'PT' | 'OT' | 'ST',
    ctx: BuilderContext,
): PatientFilter => {
    const { now, timezone } = ctx;
    return {
        isActive: { exact: true },
        hasEpisodes: episodeActivePresentOrFuture(now, timezone),
        hasVisits: {
            disciplines: { code: { exact: disciplineCode } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: date(now, timezone) },
        },
        AND: {
            // The cadence does not start until the patient has a completed visit in
            // this discipline on the current episode.
            hasVisits: {
                disciplines: { code: { exact: disciplineCode } },
                outcome: { exact: VisitOutcome.COMPLETED },
                episode: episodeActivePresentOrFuture(now, timezone),
            },
            NOT: {
                hasVisits: {
                    services: {
                        externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } },
                    },
                    disciplines: { code: { exact: disciplineCode } },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    effectiveDate: { gte: dateInDays(now, timezone, -23) },
                },
            },
            AND: {
                NOT: {
                    hasVisits: {
                        services: {
                            externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } },
                        },
                        disciplines: { code: { exact: disciplineCode } },
                        outcome: { exact: VisitOutcome.NONE },
                        effectiveDate: {
                            gte: date(now, timezone),
                            lte: dateInDays(now, timezone, 7),
                        },
                        needsAssignment: { exact: false },
                    },
                },
                AND: {
                    NOT: { hasVisits: pendingDischargeVisitFilter(disciplineCode, ctx) },
                },
            },
        },
    };
};

// Patients due a PT, OT or ST reassessment.
const PENDING_THERAPY_REEVALUATION: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.pendingTherapyReevaluation,
    label: 'Pending Therapy Re-Evaluation Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        ...pendingReEvalBranchForDiscipline('PT', ctx),
        OR: {
            ...pendingReEvalBranchForDiscipline('OT', ctx),
            OR: {
                ...pendingReEvalBranchForDiscipline('ST', ctx),
            },
        },
    }),
};

// Patients nearing their episode end with no recert or discharge visit scheduled,
// and no ROC scheduled or completed in the surrounding window.
const UPCOMING_END_OF_EPISODE: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PINNACLE_CONTAINER_KEYS.upcomingEndOfEpisode,
    label: 'Upcoming End of Episode',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        status: { exact: EpisodeStatus.ACTIVE },
        patient: { isActive: { exact: true } },
        endDate: {
            gte: date(now, timezone),
            lte: dateInDays(now, timezone, 10),
        },
        NOT: {
            hasVisits: {
                services: {
                    externalIds: {
                        value: {
                            inList: [...OASIS_DISCHARGE_SERVICE_IDS, ...OASIS_RECERT_SERVICE_IDS],
                        },
                    },
                },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            },
        },
        AND: {
            NOT: {
                hasVisits: {
                    services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    effectiveDate: {
                        gte: dateInDays(now, timezone, -5),
                        lte: dateInDays(now, timezone, 10),
                    },
                },
            },
        },
    }),
};

// Evals scheduled or completed before the patient's SOC.
const VISITS_SCHEDULED_BEFORE_SOC: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.visitsScheduledBeforeSoc,
    label: 'Visits Scheduled Before SOC',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
        episode: episodeActivePendingPresentOrFuture(now, timezone),
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            filter: {
                services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                effectiveDate: { gte: date(now, timezone) },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    gtOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Episodes carrying more than one OASIS discharge or recert visit.
const CONFLICTING_DISCHARGE_AND_RECERT_VISITS: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PINNACLE_CONTAINER_KEYS.conflictingDischargeAndRecertVisits,
    label: 'Conflicting Discharge and Recert Visits',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        ...episodeActivePresentOrFuture(now, timezone),
        hasVisits: {
            services: {
                externalIds: {
                    value: {
                        inList: [...OASIS_DISCHARGE_SERVICE_IDS, ...OASIS_RECERT_SERVICE_IDS],
                    },
                },
            },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            relatedVisitCondition: {
                exists: true,
                sameEpisode: true,
                filter: {
                    services: {
                        externalIds: {
                            value: {
                                inList: [
                                    ...OASIS_DISCHARGE_SERVICE_IDS,
                                    ...OASIS_RECERT_SERVICE_IDS,
                                ],
                            },
                        },
                    },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
    }),
};

// Upcoming OASIS discharges that still have other visits scheduled after them.
const UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.upcomingDischargeWithFuturePendingVisits,
    label: 'Upcoming Discharge With Future Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        services: { externalIds: { value: { inList: OASIS_DISCHARGE_SERVICE_IDS } } },
        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
        episode: episodeActivePresentOrFuture(now, timezone),
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            filter: {
                NOT: {
                    services: {
                        externalIds: { value: { inList: POST_DISCHARGE_EXCLUDED_SERVICE_IDS } },
                    },
                },
                outcome: { exact: VisitOutcome.NONE },
                effectiveDate: { gte: date(now, timezone) },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    gtOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Completed SOC, ROC or Eval visits with no linked order.
const COMPLETED_VISITS_MISSING_AN_ORDER: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.completedVisitsMissingAnOrder,
    label: 'Completed Visits Missing an Order',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        outcome: { exact: VisitOutcome.COMPLETED },
        hasOrder: { exact: false },
        patient: { isActive: { exact: true } },
        episode: episodeActivePresentOrFuture(now, timezone),
        services: {
            externalIds: {
                value: {
                    inList: [...SOC_SERVICE_IDS, ...ROC_SERVICE_IDS, ...EVAL_SERVICE_IDS],
                },
            },
        },
    }),
};

// Active patients with no pending visit, ignoring admin non-visit tasks.
const ACTIVE_PATIENTS_WITH_NO_VISITS: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.activePatientsWithNoVisits,
    label: 'Active Patients with No Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: episodeActivePresentOrFuture(now, timezone),
        NOT: {
            hasVisits: {
                outcome: { exact: VisitOutcome.NONE },
                NOT: {
                    services: {
                        externalIds: { value: { inList: NON_VISIT_TASK_ONLY_SERVICE_IDS } },
                    },
                },
            },
        },
    }),
};

// Active or on-hold episodes whose end date has already passed.
const UNRESOLVED_EPISODES: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PINNACLE_CONTAINER_KEYS.unresolvedEpisodes,
    label: 'Unresolved Episodes',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        endDate: { lt: date(now, timezone) },
        status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.ON_HOLD] },
    }),
};

// A completed SOC with no CMS-485 completed on any date, and none scheduled on the
// SOC's own date.
const PATIENTS_MISSING_CMS_485: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingCms485,
    label: 'Patients Missing CMS-485',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: {
            ...episodeActivePresentOrFuture(now, timezone),
            hasVisits: {
                services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
                outcome: { exact: VisitOutcome.COMPLETED },
                relatedVisitCondition: {
                    exists: false,
                    samePatient: true,
                    sameEpisode: true,
                    filter: {
                        services: { externalIds: { value: { exact: CMS_485_SERVICE_ID } } },
                        outcome: { exact: VisitOutcome.COMPLETED },
                    },
                },
                AND: {
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            services: { externalIds: { value: { exact: CMS_485_SERVICE_ID } } },
                            outcome: { exact: VisitOutcome.NONE },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gteOffsetMinutes: 0,
                                lteOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
        },
    }),
};

// A discharge inside the next week with no NOMNC completed at least 48 hours before
// it. Only near-term discharges are flagged, because a far-future discharge can
// still turn into a recertification, which needs no NOMNC.
const PATIENTS_MISSING_NOMNC: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingNomnc,
    label: 'Patients Missing NOMNCs',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: {
            ...episodeActivePresentOrFuture(now, timezone),
            hasVisits: {
                services: {
                    externalIds: { value: { inList: OASIS_DISCHARGE_ONLY_SERVICE_IDS } },
                },
                outcome: { exact: VisitOutcome.NONE },
                effectiveDate: { lte: dateInDays(now, timezone, 7) },
                NOT: {
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            services: { externalIds: { value: { inList: NOMNC_SERVICE_IDS } } },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                lteOffsetMinutes: -48 * 60,
                            },
                        ],
                    },
                },
            },
        },
    }),
};

// A completed OASIS-E2 Follow-Up with no SCIC Order completed on any date, and none
// scheduled on the Follow-Up's own date.
const PATIENTS_MISSING_SCIC_ORDER: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingScicOrder,
    label: 'Patients Missing SCIC Orders',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: {
            ...episodeActivePresentOrFuture(now, timezone),
            hasVisits: {
                services: {
                    externalIds: { value: { inList: OASIS_E2_FOLLOWUP_SERVICE_IDS } },
                },
                outcome: { exact: VisitOutcome.COMPLETED },
                relatedVisitCondition: {
                    exists: false,
                    samePatient: true,
                    sameEpisode: true,
                    filter: {
                        services: { externalIds: { value: { exact: SCIC_ORDER_SERVICE_ID } } },
                        outcome: { exact: VisitOutcome.COMPLETED },
                    },
                },
                AND: {
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            services: {
                                externalIds: { value: { exact: SCIC_ORDER_SERVICE_ID } },
                            },
                            outcome: { exact: VisitOutcome.NONE },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gteOffsetMinutes: 0,
                                lteOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
        },
    }),
};

// A completed ROC with no Post Hospital Order completed on any date, and none
// scheduled on the ROC's own date.
const PATIENTS_MISSING_POST_HOSPITAL_ORDER: QueueDefinition<RootEntity.PATIENT> = {
    key: PINNACLE_CONTAINER_KEYS.patientsMissingPostHospitalOrder,
    label: 'Patients Missing Post Hospital Orders',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: {
            ...episodeActivePresentOrFuture(now, timezone),
            hasVisits: {
                services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
                outcome: { exact: VisitOutcome.COMPLETED },
                relatedVisitCondition: {
                    exists: false,
                    samePatient: true,
                    sameEpisode: true,
                    filter: {
                        services: {
                            externalIds: { value: { exact: POST_HOSPITAL_ORDER_SERVICE_ID } },
                        },
                        outcome: { exact: VisitOutcome.COMPLETED },
                    },
                },
                AND: {
                    relatedVisitCondition: {
                        exists: false,
                        samePatient: true,
                        sameEpisode: true,
                        filter: {
                            services: {
                                externalIds: { value: { exact: POST_HOSPITAL_ORDER_SERVICE_ID } },
                            },
                            outcome: { exact: VisitOutcome.NONE },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gteOffsetMinutes: 0,
                                lteOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
        },
    }),
};

// Pending visits sent to a contract agency with no individual contractor assigned.
const VISITS_ASSIGNED_TO_CONTRACT_AGENCY: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.visitsAssignedToContractAgency,
    label: 'Visits Assigned to Contract Agency',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: {
            externalIds: { type: { exact: 'CONTRACT_AGENCY' } },
        },
    }),
};

// Episodes currently on hold, with today inside the episode window.
const ALL_TRANSFERRED_PATIENTS: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PINNACLE_CONTAINER_KEYS.allTransferredPatients,
    label: 'All Transferred Patients',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        patient: { isActive: { exact: true } },
        status: { exact: EpisodeStatus.ON_HOLD },
        isActiveByDate: { exact: true },
    }),
};

// Missed visits inside the current Medicare week, which runs Sunday to Saturday.
const MISSED_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.missedVisits,
    label: 'Missed Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => {
        const weekStart = startOfMedicareWeek(now, timezone);
        return {
            outcome: { exact: VisitOutcome.MISSED },
            effectiveDate: {
                gte: weekStart,
                lte: addCalendarDays(weekStart, 6),
            },
        };
    },
};

// Visits assigned to a clinician with no active shift on the visit's date, over a
// rolling week. The date window is end-inclusive (today .. today+6); the datetime
// window is end-exclusive on the boundary (today 00:00 .. today+7 00:00), which
// covers the same seven calendar days.
const VISITS_WITHOUT_SHIFTS: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.visitsWithoutActiveShifts,
    label: 'Visits Without Shifts',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        hasActiveShiftOnDate: { exact: false },
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: {
            employmentType: {
                inList: [EmploymentType.FULL_TIME, EmploymentType.PART_TIME],
            },
        },
        AND: {
            effectiveDate: {
                gte: date(now, timezone),
                lte: dateInDays(now, timezone, 6),
            },
            OR: {
                effectiveDate: { isNull: true },
                latestEndDatetime: {
                    gte: startOfDayInZone(date(now, timezone), timezone),
                    lt: startOfDayInZone(dateInDays(now, timezone, 7), timezone),
                },
            },
        },
    }),
};

// Pending visits that WellSky rejected for want of an authorization.
const VISITS_AWAITING_AUTHORIZATION: QueueDefinition<RootEntity.VISIT> = {
    key: PINNACLE_CONTAINER_KEYS.visitsAwaitingAuthorization,
    label: 'Scheduled Visits Awaiting Authorization',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        syncStatus: {
            state: { exact: SyncState.FAILING },
            lastFailure: {
                userSafeErrorType: { inList: AUTH_RELATED_SYNC_ERROR_TYPES },
            },
        },
    }),
};

export const PINNACLE_QUEUES: QueueDefinition[] = [
    INCOMPLETE_CARE_TEAM,
    SOC,
    ROC,
    EVAL,
    URGENT_VISITS_NEEDING_SCHEDULING,
    PATIENTS_NEEDING_SCHEDULING,
    OVERSCHEDULED_PATIENTS,
    UNDERSCHEDULED_PATIENTS,
    INACTIVE_CLINICIAN_VISITS,
    AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS,
    DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS,
    DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS,
    TRANSFERRED_PATIENTS_WITH_PENDING_VISITS,
    PENDING_HHA_SUPERVISORY,
    PENDING_LPN_SUPERVISORY,
    PENDING_THERAPY_SUPERVISORY,
    PENDING_THERAPY_REEVALUATION,
    UPCOMING_END_OF_EPISODE,
    VISITS_SCHEDULED_BEFORE_SOC,
    CONFLICTING_DISCHARGE_AND_RECERT_VISITS,
    UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS,
    COMPLETED_VISITS_MISSING_AN_ORDER,
    ACTIVE_PATIENTS_WITH_NO_VISITS,
    PATIENTS_MISSING_CMS_485,
    PATIENTS_MISSING_NOMNC,
    PATIENTS_MISSING_SCIC_ORDER,
    PATIENTS_MISSING_POST_HOSPITAL_ORDER,
    VISITS_ASSIGNED_TO_CONTRACT_AGENCY,
    ALL_TRANSFERRED_PATIENTS,
    MISSED_VISITS,
    VISITS_WITHOUT_SHIFTS,
    VISITS_AWAITING_AUTHORIZATION,
];

// UAT carries two queues production has held back: Potential Reschedules and
// Unresolved Episodes.
export const PINNACLE_UAT_QUEUES: QueueDefinition[] = [
    ...PINNACLE_QUEUES,
    POTENTIAL_RESCHEDULES,
    UNRESOLVED_EPISODES,
];
